import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useStore } from '../store/useStore';
import { uploadFormTemplatePdf, deleteFormTemplate, getFormTemplates } from '../utils/api';
import TemplateEditor from './TemplateEditor';
import styles from './TemplateManagerModal.module.css';

// Admin/PM-only modal for adding fillable-PDF templates and removing
// existing ones. The built-in Daily Report template can't be removed
// (server enforces this; we hide the delete button as well).

export default function TemplateManagerModal({ onClose, onChanged }) {
  const { project } = useStore();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);

  // Upload state
  const [name, setName]           = useState('');
  const [description, setDesc]    = useState('');
  const [file, setFile]           = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress]   = useState(0);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const fileInputRef = useRef(null);

  async function refresh() {
    if (!project) return;
    setLoading(true);
    try { setTemplates(await getFormTemplates(project.id)); }
    catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [project?.id]);

  async function handleUpload(e) {
    e.preventDefault();
    if (!file || !name.trim() || uploading) return;
    setUploading(true);
    try {
      await uploadFormTemplatePdf({
        project_id: project.id,
        name: name.trim(),
        description: description.trim(),
        file,
        onProgress: (ev) => {
          if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100));
        },
      });
      toast.success(`Added template "${name.trim()}"`);
      setName(''); setDesc(''); setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await refresh();
      onChanged?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }

  async function handleDelete(t) {
    const yes = window.confirm(
      `Delete template "${t.name}"? All submissions made against this template will also be deleted. This cannot be undone.`
    );
    if (!yes) return;
    try {
      await deleteFormTemplate(t.id);
      toast.success('Template deleted');
      await refresh();
      onChanged?.();
    } catch (err) { toast.error(err.message); }
  }

  return (
    <div className={styles.backdrop} onClick={(e) => e.target === e.currentTarget && !uploading && onClose()}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <span>Manage Form Templates</span>
          <button className={styles.closeBtn} onClick={onClose} disabled={uploading}>×</button>
        </div>

        <div className={styles.body}>
          {/* Existing templates */}
          <section className={styles.section}>
            <div className={styles.sectionLabel}>Templates</div>
            {loading ? (
              <div className={styles.empty}>Loading…</div>
            ) : (
              <div className={styles.list}>
                {templates.map((t) => (
                  <div key={t.id} className={styles.row}>
                    <div className={styles.rowMain}>
                      <span className={`${styles.kindBadge} ${t.kind === 'daily_report' ? styles.kindBuiltIn : styles.kindPdf}`}>
                        {t.kind === 'daily_report' ? 'BUILT-IN' : 'PDF'}
                      </span>
                      <div className={styles.rowText}>
                        <span className={styles.rowName}>{t.name}</span>
                        {t.description && <span className={styles.rowSub}>{t.description}</span>}
                        {t.kind === 'pdf' && (
                          <span className={styles.rowMeta}>
                            {t.schema?.fields?.length || 0} fields · {t.schema?.page_count || 0} pages
                          </span>
                        )}
                      </div>
                    </div>
                    {t.kind === 'pdf' && (
                      <>
                        <button
                          className={styles.iconBtn}
                          title="Edit template fields"
                          onClick={() => setEditingTemplate(t)}
                        >
                          <PencilIcon />
                        </button>
                        <button
                          className={styles.iconBtn}
                          title="Delete template"
                          onClick={() => handleDelete(t)}
                        >
                          <TrashIcon />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Upload new */}
          <form onSubmit={handleUpload} className={styles.section}>
            <div className={styles.sectionLabel}>Add a new PDF template</div>
            <p className={styles.blurb}>
              Upload a PDF that has fillable form fields (AcroForm).
              Users you grant permission to will see the PDF in their
              browser with each field rendered as an input. Their values
              fill the original PDF on submit.
            </p>

            <label className={styles.fieldLabel}>Template name</label>
            <input
              className={styles.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Concrete Pour Inspection"
              disabled={uploading}
            />

            <label className={styles.fieldLabel}>Description (optional)</label>
            <input
              className={styles.input}
              value={description}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="Short description shown in the template tab"
              disabled={uploading}
            />

            <label className={styles.fieldLabel}>PDF file</label>
            <div
              className={`${styles.dropzone} ${uploading ? styles.dropzoneDisabled : ''}`}
              onClick={() => !uploading && fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                style={{ display: 'none' }}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              {file
                ? <span className={styles.dzFile}>{file.name}</span>
                : <span className={styles.dzPrompt}>Click to pick a PDF</span>}
            </div>

            {uploading && (
              <div className={styles.progressWrap}>
                <div className={styles.progressTrack}>
                  <div className={styles.progressFill} style={{ width: `${progress}%` }} />
                </div>
                <span className={styles.progressText}>{progress}%</span>
              </div>
            )}

            <div className={styles.formFooter}>
              <button
                type="submit"
                className={styles.btnPrimary}
                disabled={!file || !name.trim() || uploading}
              >
                {uploading ? 'Uploading…' : 'Add Template'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Editor opens as a fixed full-screen overlay above the modal so the
          user has the whole window to design fields. Closing returns to the
          template list and refreshes (so field counts update). */}
      {editingTemplate && (
        <TemplateEditor
          template={editingTemplate}
          onClose={async () => {
            setEditingTemplate(null);
            await refresh();
            onChanged?.();
          }}
        />
      )}
    </div>
  );
}

const TrashIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <path d="M3 4h10M6 4V3h4v1M5 4l.5 9h5L11 4"
      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const PencilIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <path d="M11 2l3 3-8 8H3v-3l8-8z"
      stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
  </svg>
);
